import { withCreds } from '../authService';
import { normalizedVoiceWords } from './voiceInterruption';

const ALLOWED_STAGES = new Set([
  'voice_session_opened',
  'voice_session_closed',
  'audio_session_transition',
  'microphone_acquire_attempt',
  'microphone_acquired',
  'microphone_released',
  'microphone_reacquire_attempt',
  'microphone_reacquired',
  'microphone_reacquire_failed',
  'recognition_started',
  'recognition_ended',
  'recognition_error',
  'recorded_capture_started',
  'recorded_capture_ended',
  'recorded_capture_error',
  'tts_requested',
  'tts_audio_body_received',
  'route_handoff_started',
  'route_handoff_completed',
  'html_audio_blob_created',
  'html_audio_src_assigned',
  'html_audio_load_invoked',
  'html_audio_load_skipped',
  'html_audio_loadstart',
  'html_audio_loadedmetadata',
  'html_audio_loadeddata',
  'html_audio_canplay',
  'html_audio_canplaythrough',
  'html_audio_attempt',
  'html_audio_retry',
  'html_audio_play_requested',
  'html_audio_play_resolved',
  'html_audio_play_rejected',
  'html_audio_playing',
  'html_audio_first_positive_time',
  'html_audio_cancelled',
  'playback_started',
  'playback_completed',
  'playback_cancelled',
  'playback_failed',
  'manual_interruption',
  'false_interruption_recovery',
  'stale_callback_rejected',
  'route_thread_invalidated',
  'page_hidden',
  'page_visible',
  'candidate_started',
  'candidate_classified',
  'playback_paused',
  'candidate_rejected',
  'playback_resumed',
  'candidate_accepted',
  'replacement_dispatched',
  'backend_result',
]);

function bucketConfidence(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return 'unavailable';
  if (number < 0.25) return 'very_low';
  if (number < 0.62) return 'low';
  if (number < 0.78) return 'medium';
  return 'high';
}

function bucketWords(count) {
  if (count <= 0) return 'zero';
  if (count === 1) return 'one';
  if (count <= 3) return 'two_three';
  if (count <= 8) return 'four_eight';
  return 'nine_plus';
}

function bucketEnergy(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0.005) return 'quiet';
  if (number < 0.02) return 'low';
  if (number < 0.08) return 'medium';
  return 'high';
}

export function createVoiceIncidentRef() {
  const random = globalThis.crypto?.getRandomValues
    ? [...globalThis.crypto.getRandomValues(new Uint8Array(8))]
      .map((value) => value.toString(16).padStart(2, '0')).join('')
    : Math.random().toString(16).slice(2).padEnd(16, '0').slice(0, 16);
  return `vi_${random}`;
}

export function voiceBrowserFamily() {
  const ua = String(globalThis.navigator?.userAgent || '');
  if (/Firefox\//i.test(ua)) return 'firefox';
  if (/AppleWebKit\//i.test(ua) && /Safari\//i.test(ua) && !/(Chrome|Chromium|CriOS)\//i.test(ua)) return 'webkit';
  if (/(Chrome|Chromium|CriOS)\//i.test(ua)) return 'chromium';
  return 'other';
}

export function voiceBrowserMetadata() {
  const ua = String(globalThis.navigator?.userAgent || '');
  const platform = String(globalThis.navigator?.platform || '');
  const touchPoints = Number(globalThis.navigator?.maxTouchPoints || 0);
  const family = voiceBrowserFamily();
  const version = family === 'webkit'
    ? ua.match(/Version\/(\d+)/i)?.[1]
    : null;
  let appleDeviceClass = 'non_apple';
  if (/iPhone|iPod/i.test(ua)) appleDeviceClass = 'iphone';
  else if (/iPad/i.test(ua) || (platform === 'MacIntel' && touchPoints > 1)) appleDeviceClass = 'ipad';
  else if (family === 'webkit') appleDeviceClass = 'apple_desktop';
  return Object.freeze({
    browser_family: family,
    browser_version_bucket: version ? `major_${version}` : 'unavailable',
    apple_device_class: appleDeviceClass,
  });
}

export function bucketPlaybackOffset(value) {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds < 0) return 'unavailable';
  if (seconds === 0) return 'zero';
  if (seconds < 5) return 'under_5s';
  if (seconds < 30) return '5_30s';
  return '30s_plus';
}

export function summarizeVoiceIncidentEvidence({
  candidate,
  assistantText,
  confidence,
  isFinal,
  microphoneEnergy,
} = {}) {
  const heard = normalizedVoiceWords(candidate);
  const spoken = normalizedVoiceWords(assistantText);
  const spokenSet = new Set(spoken);
  const shared = heard.filter((word) => spokenSet.has(word)).length;
  const overlap = heard.length ? shared / heard.length : 0;
  const contained = Boolean(heard.length && spoken.join(' ').includes(heard.join(' ')));
  let overlapBucket = 'none';
  if (contained) overlapBucket = 'contained';
  else if (overlap >= 0.72) overlapBucket = 'high';
  else if (overlap >= 0.4) overlapBucket = 'medium';
  else if (overlap > 0) overlapBucket = 'low';
  return Object.freeze({
    recognition_state: isFinal ? 'final' : 'interim',
    confidence_bucket: bucketConfidence(confidence),
    word_count_bucket: bucketWords(heard.length),
    overlap_bucket: overlapBucket,
    microphone_energy_bucket: bucketEnergy(microphoneEnergy),
  });
}

export function appliedAudioConstraint(value) {
  if (value === true) return 'applied';
  if (value === false) return 'not_applied';
  return 'unavailable';
}

export function emitVoiceIncident(event = {}) {
  const stage = String(event.stage || '');
  if (!ALLOWED_STAGES.has(stage) || !/^vi_[0-9a-f]{16}$/.test(String(event.incident_ref || ''))) {
    return false;
  }
  const browser = voiceBrowserMetadata();
  const boundedRef = (value) => (/^vi_[0-9a-f]{16}$/.test(String(value || '')) ? String(value) : '');
  const payload = Object.freeze({
    stage,
    incident_ref: event.incident_ref,
    browser_family: event.browser_family || browser.browser_family,
    browser_version_bucket: String(event.browser_version_bucket || browser.browser_version_bucket),
    apple_device_class: String(event.apple_device_class || browser.apple_device_class),
    voice_session_ref: boundedRef(event.voice_session_ref),
    thread_owner_ref: boundedRef(event.thread_owner_ref),
    monitoring_mode: String(event.monitoring_mode || 'none'),
    playback_state: String(event.playback_state || 'inactive'),
    recognition_state: String(event.recognition_state || 'unavailable'),
    confidence_bucket: String(event.confidence_bucket || 'unavailable'),
    word_count_bucket: String(event.word_count_bucket || 'zero'),
    overlap_bucket: String(event.overlap_bucket || 'none'),
    microphone_energy_bucket: String(event.microphone_energy_bucket || 'quiet'),
    echo_cancellation: String(event.echo_cancellation || 'unavailable'),
    noise_suppression: String(event.noise_suppression || 'unavailable'),
    auto_gain_control: String(event.auto_gain_control || 'unavailable'),
    classifier_action: String(event.classifier_action || 'unavailable'),
    classifier_reason: String(event.classifier_reason || 'unavailable'),
    owner_valid: event.owner_valid === true,
    candidate_sequence: Math.max(0, Number(event.candidate_sequence) || 0),
    recognition_generation: Math.max(0, Number(event.recognition_generation) || 0),
    ai_generation: Math.max(0, Number(event.ai_generation) || 0),
    replacement_request_id: String(event.replacement_request_id || '').slice(0, 96),
    backend_accepted: event.backend_accepted === true,
    backend_duplicate: event.backend_duplicate === true,
    audio_session_state: String(event.audio_session_state || 'unavailable'),
    transition_result: String(event.transition_result || 'unavailable'),
    microphone_state: String(event.microphone_state || 'unavailable'),
    interruption_mode: String(event.interruption_mode || 'unavailable'),
    playback_offset_bucket: String(event.playback_offset_bucket || 'unavailable'),
    media_ready_state: String(event.media_ready_state || 'unavailable'),
    media_network_state: String(event.media_network_state || 'unavailable'),
    media_source_phase: String(event.media_source_phase || 'unavailable'),
    media_position_state: String(event.media_position_state || 'unavailable'),
    playback_attempt: Math.max(0, Math.min(8, Number(event.playback_attempt) || 0)),
    media_event_elapsed_ms: Math.max(
      0,
      Math.min(120000, Math.round(Number(event.media_event_elapsed_ms) || 0)),
    ),
    stale_callback: event.stale_callback === true,
    invalidation_reason: String(event.invalidation_reason || 'unavailable'),
  });
  console.info('[VOICE_INCIDENT]', JSON.stringify(payload));
  try {
    void Promise.resolve(withCreds('/api/voice/incident', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    })).catch(() => {});
  } catch (_) {
    // Observability is fail-open and cannot alter the voice lifecycle.
  }
  return true;
}
